/** Platform Console strings. Copy is taken from the deck, not paraphrased. */
export const platform = {
  // --- shell ---
  'p.brand': 'Cattle Book',
  'p.brandSub': 'Platform Console',
  'p.nav.platform': 'Platform',
  'p.nav.operations': 'Operations',
  'p.nav.you': 'You',
  'p.nav.overview': 'Overview',
  'p.nav.businesses': 'Businesses',
  'p.nav.subscriptions': 'Subscriptions',
  'p.nav.plans': 'Plans',
  'p.nav.usage': 'Usage & storage',
  'p.nav.health': 'System health',
  'p.nav.support': 'Support',
  'p.nav.accessLog': 'Access log',
  'p.nav.settings': 'Console settings',

  // --- P-01 Overview ---
  'p.overview.title': 'Platform',
  'p.overview.thisMonth': 'This month',
  'p.overview.addBusiness': 'Add a business',
  'p.overview.pastDueTitle': '{count} businesses are past due',
  // The payload carries the count, the amount and the share — and nothing about
  // how late the oldest one is. "One is more than two weeks late" was the deck's
  // invention, and it is the sentence a reader would act on first.
  'p.overview.pastDueBody': '{amount} at risk — {share} of your revenue.',
  'p.overview.review': 'Review',
  'p.overview.monthlyRevenue': 'Monthly revenue',
  // The month is derived from the payload's own `today`, not typed in: the
  // contract says this delta is "change against last month".
  'p.overview.revenueDelta': '{amount} on {month}',
  'p.overview.paying': 'Paying businesses',
  'p.overview.payingSub': '{amount} average each',
  'p.overview.onTrial': 'On trial',
  // CONTRACT GAP: P1 carries `trialCount` and nothing about when a trial ends,
  // so "1 trial ends today" could not be told from "none do".
  'p.overview.trialSub': 'Not paying anything yet',
  'p.overview.animals': 'Animals tracked',
  'p.overview.animalsSub': 'Across all businesses',
  'p.overview.underManagement': 'Cattle under management',
  'p.overview.underManagementSub': 'Total cost basis across all {count} books',
  'p.overview.profitBooked': 'Profit booked by traders',
  'p.overview.profitBookedSub': 'This season, all businesses',
  'p.overview.yourCut': 'Your cut of it',
  'p.overview.yourCutSub': '{revenue} a month against {profit} they made',
  'p.overview.payingChart': 'Paying businesses',
  'p.overview.lastSixMonths': 'Last six months',
  'p.overview.newThisMonth': 'New this month',
  'p.overview.trialsConverted': 'Trials converted',
  'p.overview.cancelled': 'Cancelled',
  'p.overview.newest': 'Newest businesses',
  'p.overview.storage': 'Storage',
  // P1 has no growth rate (that is P13's `growthBytesPerMonth`), so the
  // projection is gone rather than guessed.
  'p.overview.storageBody':
    'Photographs and video, mostly — the ledgers themselves weigh almost nothing.',
  'p.overview.backups': 'Nightly backups',
  // "One retry was needed for Rana Mandi Traders" named a business that does not
  // exist and an event nothing reports. P1 carries the two counts, so the line
  // says what they are; the retry count lives on P14 and is said there.
  'p.overview.backupsBody': 'Every business backed up last night.',
  'p.overview.backupsBodyPartial': '{count} of {total} backed up last night.',
  'p.overview.videoQueue': 'Video processing',
  'p.overview.videoQueueValue': 'in the queue',
  // The count is `videoFailed24h`; the cause and the reassurance were invented.
  'p.overview.videoQueueBody': '{count} failed in the last day.',
  'p.overview.videoQueueBodyNone': 'Nothing failed in the last day.',
  'p.overview.empty': 'No businesses yet',
  'p.overview.emptyBody': 'When a trader signs up, this is where their first month appears.',

  // --- P-02 Businesses ---
  'p.businesses.title': 'Businesses',
  'p.businesses.subtitle': '{count} cattle traders use Cattle Book',
  'p.businesses.searchPlaceholder': 'Business, owner or city',
  'p.businesses.filterAll': 'All {count}',
  'p.businesses.filterPaying': 'Paying {count}',
  'p.businesses.filterTrial': 'Trial {count}',
  'p.businesses.filterPastDue': 'Past due {count}',
  'p.businesses.filterSuspended': 'Suspended {count}',
  'p.businesses.colBookValue': 'Book value',
  'p.businesses.colPays': 'Pays',
  'p.businesses.since': 'since {when}',
  'p.businesses.accessTitle': 'You have full access to every book',
  'p.businesses.access1': 'Every animal, purchase price, sale price, cost and profit',
  'p.businesses.access2': 'Photographs, videos, expenses, buyers and sellers',
  'p.businesses.access3': 'Open any book read-only, or edit it to fix a support problem',
  'p.businesses.access4': 'Every book you open is written to the access log with the reason',
  'p.businesses.emptyTitle': 'No businesses match',
  'p.businesses.emptyBody': 'Try a different word, or clear the filters.',

  // --- P-03 Business detail ---
  'p.business.joined': '{city} · joined {date} · owner {owner}',
  'p.business.openBook': 'Open their book',
  'p.business.openNamedBook': "Open {name}'s book",
  'p.business.planTile': 'Plan',
  'p.business.animalsOf': 'of {limit} allowed',
  'p.business.peopleOf': 'of {limit} allowed',
  'p.business.storageSub': 'Photos and video',
  'p.business.payments': 'Payments',
  'p.business.paymentsUpToDate': 'Up to date',
  'p.business.invoices': 'Invoices',
  'p.business.nextCharge': 'Next charge',
  'p.business.method': 'Method',
  'p.business.peoplePanel': 'People in this business',
  'p.business.theirBook': 'Their book',
  'p.business.openIt': 'Open it',
  'p.business.moneyTiedUp': 'Money tied up',
  'p.business.costingThem': 'Costing them',
  'p.business.profitBooked': 'Profit booked',
  'p.business.soldThisSeason': 'Sold this season',
  'p.business.owedToThem': 'Owed to them',
  'p.business.openLogged':
    'Opening a book is recorded in the access log with the reason you give. If he ever disputes a figure, that log is what settles it.',
  'p.business.health': 'Health',
  'p.business.backedUp': 'Backed up',
  'p.business.failedUploads': 'Failed uploads',
  'p.business.appVersion': 'App version',
  'p.business.lastActivity': 'Last activity',
  'p.business.dangerBody': 'These affect a real business that is trading today.',
  'p.business.extendTrial': 'Extend the trial',
  'p.business.notOnTrial': 'Not on trial',
  'p.business.deleteAll': 'Delete everything',
  'p.business.deleteBody': 'Their whole book. Never reversible.',
  'p.business.deleteAction': 'Delete…',
  // Not "shown to them when they try to make a change": the refusal a trader
  // sees is the API's own sentence, and it carries no reason. This one is
  // written down for whoever asks later.
  'p.business.restrictReasonHint': 'Recorded against your name in the audit log, which is where the answer to "why?" is kept.',
  'p.business.deleteTitle': 'Delete {name} and everything in it?',
  'p.business.deleteWarning':
    'Every animal, price, photograph and video is destroyed. There is no backup you can restore this from.',
  'p.business.notFound': 'No such business',
  'p.business.notFoundBody': 'It may have been deleted, or the link is wrong.',

  // --- P-04 Inside a business ---
  'p.inside.viewing': 'Viewing',
  'p.inside.bannerTitle': "You are inside {name}'s book",
  'p.inside.readOnly': 'Read-only',
  'p.inside.editing': 'Editing',
  'p.inside.switchToEdit': 'Switch to edit',
  'p.inside.leave': 'Leave',
  'p.inside.elapsed': '{minutes} min in',
  'p.inside.reasonTitle': 'Why are you opening this book?',
  'p.inside.reasonBody':
    'This is written to the access log, shown in the owner’s own history, and cannot be edited afterwards — by anyone, including you.',
  'p.inside.reasonLabel': 'Reason',
  // An example, not a record — so it no longer carries a ticket number that
  // would read as a real one.
  'p.inside.reasonPlaceholder': 'Checking a reported break-even',
  'p.inside.reasonRequired': 'A reason is required before a book can be opened.',
  'p.inside.openReadOnly': 'Open read-only',
  'p.inside.editTitle': 'Switch to editing {name}’s book?',
  'p.inside.editBody':
    'Anything you change is his record, not yours. The access log will show this visit as edited.',
  'p.inside.headerSub': '{inStock} in stock · {sold} sold · {city}',
  'p.inside.thisSeason': 'This season',
  'p.inside.exportBook': 'Export his book',
  'p.inside.moneyTiedUp': 'Money tied up',
  'p.inside.moneyTiedUpSub': 'Purchase plus everything since',
  'p.inside.costingHim': 'Costing him',
  'p.inside.costingHimSub': 'Feed, rent and labour',
  'p.inside.profitBooked': 'Profit booked',
  // Both figures are the dashboard's own: `soldThisSeason` and
  // `profitBookedDaysHeld`, which the contract calls "the tile's sub-line".
  'p.inside.profitBookedSub': '{count} sold, held {days} days',
  'p.inside.owedToHim': 'Owed to him',
  'p.inside.animals': 'His animals',
  'p.inside.animalsSub': 'Everything he sees, you see',
  'p.inside.colBreedTeeth': 'Breed & teeth',
  'p.inside.colDays': 'Days',
  'p.inside.colPaid': 'Paid',
  'p.inside.colCostNow': 'Cost now',
  'p.inside.colBreakEven': 'Break-even',
  'p.inside.colAsking': 'Asking',
  'p.inside.monthlyCosts': 'His monthly costs',
  'p.inside.perAnimalPerDay': 'Per animal, per day',
  'p.inside.whySupport': 'Why support was called',
  'p.inside.ticket': 'Ticket',
  'p.inside.raisedBy': 'Raised by',
  'p.inside.about': 'About',
  'p.inside.answer': 'Answer',

  // --- P-05 Plans ---
  'p.plans.title': 'Plans',
  'p.plans.subtitle': 'What a cattle trader pays, and what he gets',
  'p.plans.newPlan': 'New plan',
  'p.plans.free': 'Free',
  'p.plans.trialLength': '· {days} days',
  'p.plans.onTrialNow': '{count} on trial now',
  'p.plans.businessesRevenue': '{count} businesses · {amount}',
  'p.plans.revenueTitle': 'Where the revenue comes from',
  // The deck's "45% of your customers and 21% of your money" was a figure for a
  // fictional book of business. The bars above are the real revenue split.
  'p.plans.revenueNote':
    'The plan with the most customers on it is rarely the plan with the most money in it. These bars are revenue, not head count.',
  'p.plans.howTheyPay': 'How they pay',
  // "30 of 31 customers" was invented. The bars above are the real mix: the
  // rails money has actually arrived on, from P9's `GET /platform/payment-mix`
  // — not the methods businesses have registered, which is what this tile read
  // while it was being filled from the settings payload.
  'p.plans.payNote':
    'These are the rails traders actually pay on. Anything that assumes a card — a card-only checkout, a foreign payment processor — fits almost none of them.',
  'p.plans.priceChangeTitle': 'Change the price of {name}?',
  'p.plans.priceChangeBody':
    '{count} businesses pay for this plan today. They will be charged the new price on their next renewal, not now.',
  'p.plans.businessesCount': '{count} businesses',

  // --- P-06 Subscriptions ---
  'p.subs.title': 'Subscriptions',
  'p.subs.subtitle': 'Who pays, who has not, and what renews next',
  'p.subs.statement': 'Statement',
  'p.subs.chaseAll': 'Chase all overdue',
  'p.subs.collected': 'Collected this month',
  'p.subs.collectedSub': '{paid} of {total} have paid',
  'p.subs.overdue': 'Overdue',
  'p.subs.overdueSub': '{count} businesses · {share} of revenue',

  'p.subs.renewsSub': '{count} businesses',
  'p.subs.trialsEnding': 'Trials ending',
  // CONTRACT GAP: `trialsEnding` is one count with no window on it, so "1 today,
  // 2 this week" cannot be told apart from the payload.
  'p.subs.trialsEndingSub': 'Not paying anything yet',
  'p.subs.notPaid': 'Not paid',
  'p.subs.notPaidSub': '{count} businesses',
  'p.subs.colLateBy': 'Late by',
  'p.subs.colLastTried': 'Last tried',
  'p.subs.lateDays': '{count} days',
  'p.subs.seasonNote':
    'A trader who cannot pay still has a herd to feed. Suspending leaves his book readable but frozen — he can see what he owes, and cannot record the sale that would pay it. A phone call collects more than a freeze does.',
  'p.subs.renewingThisWeek': 'Renewing this week',
  'p.subs.moreRenewals': '+{count} more',
  'p.subs.whyFail': 'Why payments fail here',
  // The retry gap is a setting on P-11 and is not loaded here, so it is not
  // quoted here either.
  'p.subs.failNote':
    'Nearly every failure is an empty wallet, not a refusal. Retrying days later collects more than retrying the same hour.',
  'p.subs.leaveIt': 'Leave it',
  'p.subs.recordPayment': 'Record payment',
  // C10: this used to say suspension "closes a book". It does not, and never
  // will again — the sentence has to agree with `p.subs.seasonNote` two lines
  // of screen above it and with `p.business.suspendWarning` on P-03.
  'p.subs.restrictMidSeason':
    'It is the middle of a season. Suspending {name} does not close his book — he can still open it and read every figure in it — but nothing new goes into it until you lift it, including the sale he makes this afternoon. A phone call collects more than a freeze does.',
  'p.subs.emptyTitle': 'Everyone has paid',
  'p.subs.emptyBody': 'No business is overdue. This is the screen you want to be boring.',

  // --- P-07 Usage & storage ---
  'p.usage.title': 'Usage & storage',
  'p.usage.subtitle': 'Who is outgrowing their plan, and what it costs you to host them',
  'p.usage.storageUsed': 'Storage used',
  'p.usage.storageUsedSub': 'of {total} · {percent}',
  'p.usage.growingBy': 'Growing by',
  // CONTRACT GAP: nothing in P13 says when the store would be full, and a date
  // projected in the browser is a prediction, not a figure the server gave.
  'p.usage.growingSub': 'How fast the store is filling',
  'p.usage.costsYou': 'Costs you',
  'p.usage.costsYouSub': '{share} of revenue',
  'p.usage.nearLimit': 'Near a limit',
  'p.usage.nearLimitSub': 'Businesses to move up a plan',
  'p.usage.closeToLimits': 'Close to their limits',
  'p.usage.closeToLimitsSub': 'Each one is a plan upgrade waiting to be offered',
  'p.usage.colSuggest': 'Suggest',
  'p.usage.roomToSpare': 'Room to spare',
  'p.usage.unlimited': 'unlimited',
  'p.usage.ofLimit': '{used} of {limit}',
  'p.usage.suggestUpgrade': '{plan} · +{amount}',
  'p.usage.whereStorageGoes': 'Where the storage goes',
  // "all 47 of them", "400 MB", "411 GB" were deck figures. The bars beside this
  // line are the real breakdown, so the note says what shape it has, not what
  // size it is.
  'p.usage.storageNote':
    'A trader’s ledger is text and weighs almost nothing. Everything large here is photographs and film of cattle.',
  'p.usage.lever': 'The lever',
  'p.usage.originalsKept': 'Videos kept at original size',
  // The lever is `keepOriginalVideos: false` — keep the transcode, not the
  // original. There is no 90-day rule behind this figure.
  'p.usage.ifDropped': 'If originals were not kept',
  'p.usage.billAfter': 'Storage bill after',
  'p.usage.leverNote':
    'Traders keep the 720p copy either way. Deleting originals after a season would halve your hosting bill and no one would notice — but it is irreversible, so it needs to be their choice, not a silent default.',
  'p.usage.leverConfirmTitle': 'Stop keeping original videos?',
  // The weight of the confirm is kept — it IS irreversible once a purge runs —
  // but it now describes the POLICY being set rather than asserting a deletion
  // that has not happened. Nothing on the server removes an original today.
  'p.usage.leverConfirmBody':
    'This sets the rule for every business: an original upload is not kept once its 720p copy exists. The 720p copy each trader watches is kept either way. An original that has gone cannot be recovered, so the rule is not something to undo later.',

  // --- P-08 System health ---
  'p.health.title': 'System health',
  'p.health.subtitle': 'Everything running, across all {count} businesses',
  'p.health.allGood': 'All good',
  'p.health.backedUpLastNight': 'Backed up last night',
  'p.health.backedUpSub': '{count} needed a retry',
  'p.health.backedUpSubNone': 'None needed a retry',
  'p.health.videosQueued': 'Videos in the queue',
  'p.health.videosQueuedSub': 'Longest waiting {count} min',
  'p.health.videosQueuedNone': 'Nothing waiting',
  'p.health.failed24h': 'Failed in 24h',
  // The count is real; "both oversized uploads" was a guess at a cause nothing
  // in the payload reports.
  'p.health.failed24hSub': 'Uploads that did not finish',
  'p.health.storageUsedSub': '{percent} of {total}',
  'p.health.needsALook': 'Needs a look',
  'p.health.colAction': 'Action',
  'p.health.succeeded': 'Succeeded',
  'p.health.suggestPlan': 'Suggest {plan}',
  'p.health.biggestStores': 'Biggest stores',
  'p.health.storesNote':
    'Video is nearly all of it. Transcoding down to 720p is what keeps this from being far larger.',
  'p.health.emptyTitle': 'Nothing needs a look',
  'p.health.emptyBody': 'Every backup ran and no upload failed in the last day.',

  // --- P-09 Support ---
  'p.support.title': 'Support',
  // The open count is on the filter pill, where it describes the rows actually
  // loaded. A total in the subtitle would be arithmetic over one page of a
  // cursor-paged list, which is not a total.
  'p.support.subtitle': 'Most arrive on WhatsApp, not email.',
  'p.support.logCall': 'Log a call',
  'p.support.searchPlaceholder': 'Business, person or subject',
  'p.support.filterOpen': 'Open {count}',
  'p.support.filterAnswered': 'Answered {count}',
  // TICKET_STATUSES is ['OPEN','ANSWERED','CLOSED']. A closed ticket is not an
  // answered one, and folding the two together is what made the Answered pill
  // count closed tickets in the first place.
  'p.support.filterClosed': 'Closed {count}',
  'p.support.closed': 'Closed',
  'p.support.colFrom': 'From',
  'p.support.colAbout': 'About',
  'p.support.colCameIn': 'Came in',
  'p.support.colWaiting': 'Waiting',
  'p.support.openHisBook': 'Open his book',
  'p.support.answered': 'Answered',
  'p.support.view': 'View',
  'p.support.breakdown': 'The figures behind it',
  'p.support.openToCheck': 'Open his book to check',
  'p.support.whatPeopleAsk': 'What people ask about',
  // "Nine video complaints in a month" was invented; the bars above are the real
  // categories, counted server-side across EVERY ticket (P15 · `GET
  // /platform/support/asked-about`). They used to be counted from the page of
  // tickets on screen, which made the tallest bar a property of the scroll
  // position.
  'p.support.askNote':
    'The tallest bar is usually a setting, not a fault. Changing it is cheaper than answering it again.',
  // The deck's line was "It goes to him on WhatsApp." Nothing goes anywhere
  // (G22), and a support agent who believes it does will not pick up the phone.
  'p.support.replyPlaceholder': 'Type your reply. It is recorded on the ticket, not sent to him.',
  'p.support.sendReply': 'Send reply',
  'p.support.closeTicket': 'Close ticket',
  'p.support.emptyTitle': 'No open tickets',
  'p.support.emptyBody': 'Nothing is waiting on you. Log a call if someone rings.',

  // --- P-10 Access log ---
  'p.log.title': 'Access log',
  'p.log.subtitle': 'Every time anyone from your side opened a trader’s book',
  'p.log.booksOpened': 'Books opened',
  // CONTRACT GAP: the console passes no `from`/`to`, and `accessLogResponse`
  // does not say what window `booksOpened` covers. "Last 30 days" was a claim
  // about a range nobody asked for.
  'p.log.booksOpenedSub': 'Every visit in this log',
  'p.log.anythingEdited': 'Anything edited',
  'p.log.anythingEditedSub': 'Visits that changed something',
  'p.log.averageVisit': 'Average visit',
  'p.log.averageVisitSub': 'Sessions end by themselves after an hour',
  'p.log.ownersNotified': 'Owners notified',
  'p.log.ownersNotifiedSub': 'It appears in their own history',
  'p.log.searchPlaceholder': 'Business or person',
  'p.log.filterEverything': 'Everything',
  'p.log.filterEdited': 'Edited something',
  'p.log.filterNoTicket': 'No ticket',
  'p.log.colWhoseBook': 'Whose book',
  'p.log.readOnly': 'Read only',
  'p.log.edited': 'Edited',
  'p.log.immutable': 'Rows cannot be edited or deleted, including by you.',
  'p.log.immutableWhy':
    'That is what makes the log worth anything the day a trader disputes a figure.',
  'p.log.noTicketTitle': 'Some visits here have no ticket number',
  // The business and the date were both invented. The panel now appears only
  // when such a row is actually on screen, and names none of them — the filter
  // above is how they are found.
  'p.log.noTicketBody':
    'A book can be opened without a support request. That is allowed — it is your platform — but it is the kind of entry a trader would ask about, so the filter above exists to find them.',
  'p.log.emptyTitle': 'No book has been opened',
  'p.log.emptyBody': 'Nothing to show yet. Every visit will land here with its reason.',

  // --- P-11 Console settings ---
  'p.settings.title': 'Console settings',
  'p.settings.subtitle': 'How the platform behaves for all {count} businesses',
  'p.settings.readOnlyNotice':
    'You can read every setting here. Changing one is the platform owner’s to do.',
  'p.settings.team': 'Your team',
  'p.settings.addSomeone': 'Add someone',
  'p.settings.colCanDo': 'Can do',
  'p.settings.colBooksOpened': 'Books opened',
  'p.settings.you': 'you',
  'p.settings.support': 'support',
  'p.settings.everything': 'Everything',
  'p.settings.teamNote':
    'Support can open a book and reply to tickets. Only you can change plans, suspend a business or delete anything.',
  'p.settings.trials': 'Trials',
  'p.settings.trialLength': 'How long a trial lasts',
  'p.settings.askForMethod': 'Ask for a payment method up front',
  'p.settings.askForMethodHint': 'Off means more trials, fewer conversions',
  'p.settings.keepData': 'Keep their data after a trial ends',
  'p.settings.keepDataHint': 'So they can come back next season',
  'p.settings.howYouGetPaid': 'How you get paid',
  'p.settings.usedBy': '{count} businesses use it',
  'p.settings.usedByShort': '{count} businesses',
  'p.settings.manualNote': '{count} businesses · you confirm each one by hand',
  'p.settings.live': 'Live',
  'p.settings.manual': 'Manual',
  'p.settings.retryAfter': 'Retry a failed payment after',
  'p.settings.retryNote':
    '{count} days, not the same day — nearly every failure here is an empty wallet.',
  'p.settings.limits': 'Limits for everyone',
  'p.settings.largestVideo': 'Largest video',
  // CONTRACT GAP: no route serves `askedAboutResponse`, and nothing counts
  // support questions by subject over a month. The hint now says what the
  // setting does instead of quoting a figure nobody computed.
  'p.settings.largestVideoHint': 'A bigger upload than this is refused on the trader’s phone',
  'p.settings.keepOriginals': 'Keep original videos',
  'p.settings.keepOriginalsHint': 'Traders keep the 720p copy regardless',
  'p.settings.forever': 'Forever',
  // The other end of `keepOriginalVideos`, when no `keepOriginalVideoDays` is
  // set. It is a policy, not a countdown.
  'p.settings.notKept': 'Not kept',
  'p.settings.backupAt': 'Nightly backup at',
  'p.settings.dangerBody': 'These affect every business at once.',
  'p.settings.maintenance': 'Maintenance mode',
  'p.settings.maintenanceBody': 'Nobody can sign in. Use only for an upgrade.',
  'p.settings.turnOn': 'Turn on',
  'p.settings.maintenanceConfirmTitle': 'Turn on maintenance mode?',
  'p.settings.maintenanceConfirmBody':
    'Nobody can sign in — not a trader, not his staff, not you on the phone app. Anyone already signed in is stopped at their next action.',
  'p.settings.broadcast': 'Message every owner',
  // G22 · no provider, no template, no consent model, and P19 stores nothing.
  // The copy therefore says what the button does — compose and queue — and
  // never that {count} owners were reached.
  'p.settings.broadcastBody': 'Composes one message for all {count} owners',
  'p.settings.compose': 'Compose…',
  'p.settings.broadcastTitle': 'Message every owner',
  'p.settings.broadcastLabel': 'Your message',
  'p.settings.broadcastPreview': 'What {count} owners would receive:',
  'p.settings.broadcastSend': 'Send to {count} owners',

  // --- P-02 ---
  // CONTRACT GAP: no `GET /platform/businesses/export`. The file is written in
  // the browser from the rows on screen, so the toast says that and says it is
  // the FILTERED view — never that the server prepared a complete export.
  'p.businesses.exportFilename': 'businesses',
  'p.businesses.exported':
    '{count} rows written to a CSV file in your browser \u2014 the businesses this filter is showing.',
  'p.businesses.exportEmpty': 'No business matches this filter, so there is nothing to export.',
  'p.businesses.statusActive': 'Active',
  'p.businesses.statusPastDue': 'Past due',
  'p.businesses.statusSuspended': 'Suspended',
  // Dunning's own escalation. Since C10 it means to a trader exactly what
  // Suspended means; the word differs because who did it differs.
  'p.businesses.statusReadOnly': 'Read-only',
  'p.businesses.statusEndsToday': 'Ends today',
  'p.businesses.statusDaysLeft': '{count} days left',

  // --- P-03 ---
  'p.business.planSub': '{amount} a month',
  'p.business.soldOf': '{sold} of {total}',
  'p.business.trialEndsOn': 'Trial ends {date}',
  'p.business.deleteToast': '{name} and everything in it is deleted.',
  // There is no email route and no messaging provider (G22), so this opens the
  // reader's OWN mail client and sends nothing from here. It cannot address the
  // draft either: no schema in the contract carries an email address —
  // `businessSummarySchema` has ownerName and a nullish ownerPhone, and
  // `personSchema` has a username. So the To line is blank, and the toast says
  // so rather than reporting a message as sent.
  'p.business.emailSubject': 'Cattle Book \u2014 {name}',
  'p.business.emailToast':
    'A draft is open in your mail app \u2014 nothing has been sent from here. The platform holds no email address for {owner}, so the To line is blank.',
  'p.business.noInvoicesTitle': 'No payments yet',
  'p.business.noInvoicesBody': 'Nothing has been charged to this business.',
  'p.business.noPeopleTitle': 'Nobody has been added',
  'p.business.noPeopleBody': 'The owner is the only account until they add someone.',
  'p.business.noBookTitle': 'No book to open yet',
  'p.business.noBookBody':
    'This business has not recorded an animal, so there is nothing to open.',

  // --- P-04 ---
  // The feed note is three fragments rather than one sentence with {estimate}
  // and {gap} placeholders: both are money, and money is composed as <Money>
  // elements around the words, never formatted into a t() variable.
  'p.inside.tagNumber': 'Tag {tag}',
  'p.inside.bannerOpened': 'Opened {when} \u00b7 reason: \u201c{reason}\u201d.',
  // CONTRACT GAP: the dashboard gives a receivable TOTAL and no debtor, so the
  // deck's "Rana Tariq · 30 days" named a buyer nothing reports.
  'p.inside.owedToHimSub': 'Sold, and not yet paid for',
  'p.inside.feedNoteEstimate': 'His feed estimate is still set to',
  'p.inside.feedNoteGap': 'He is under-counting by',
  'p.inside.feedNoteGapUnit': 'a day per animal \u2014 worth telling him.',
  'p.inside.answerTail': 'Then commission solved on the sale price. The figure is right.',
  // CONTRACT GAP: there is no platform book-export route (`GET /reports/export`
  // is the trader's own). The animals table is written in the browser from the
  // rows on screen, and a figure the payload did not carry is left BLANK — a
  // zero would be a claim about a trader's money, in a file that outlives the
  // session.
  'p.inside.colSoldFor': 'Sold for',
  'p.inside.exportFilename': 'book',
  'p.inside.exported':
    '{count} animals written to a CSV file in your browser \u2014 the rows shown for {name}.',
  'p.inside.exportEmpty': 'There is no animal in this book to export.',
  'p.inside.exportBlank':
    'Some figures were not in this book\u2019s payload, so those cells are left blank rather than written as zero.',
  'p.inside.toastEditing':
    'You are now editing {name}\u2019s book. Every change is written to the access log.',

  // --- P-05 ---
  // Plan names are owner-editable data and come from the API; these are the
  // fallback labels for a code whose plan record has not loaded.
  'p.plan.TRIAL': 'Trial',
  'p.plan.BASIC': 'Basic',
  'p.plan.TRADER': 'Trader',
  'p.plan.FARM': 'Farm',

  // --- PLAN_FEATURES · codes are stored, labels are looked up (§ enums.ts) ---
  'p.planFeature.OWNER_CONSOLE': 'Owner console on the web',
  'p.planFeature.REPORTS': 'Reports and exports',
  'p.planFeature.PRICE_LOCK': 'Price Lock',
  'p.planFeature.LOTS': 'Buy in lots',
  'p.planFeature.PARTNER_SHARES': 'Partner shares',

  // INVOICE_METHODS carries CARD, which is not a PAYMENT_METHOD_CODE and so has
  // no label in the shared dictionary. D3: no card is stored in v1 either way.
  'paymentMethod.CARD': 'Card',
  'p.plans.priceChanged':
    '{name} repriced. Businesses on it are charged the new price at their next renewal.',
  'p.plans.planCreated': '{name} created.',

  // --- P-06 ---
  'p.subs.cityAnimals': '{city} \u00b7 {count} animals',
  'p.subs.countOf': '{count} of {of}',
  'p.subs.toastCall': 'Calling {name}.',
  // G22. `POST /invoices/:id/remind` writes an audit row and stamps the
  // invoice; the only notifier there is answers `NO_PROVIDER`. "A reminder went
  // to {name}" would have whoever pressed this stop chasing a man nobody
  // contacted, so the toast says what the server actually did.
  'p.subs.toastRemind':
    'Reminder logged against {name}. Nothing was sent — no WhatsApp provider is set up.',
  'p.subs.toastRetry': 'Payment retried for {name}.',
  'p.subs.toastLeave': '{name} left alone. It retries by itself tonight.',
  // No statement route exists. The file is written in the browser from the rows
  // already on screen, so the toast says that and nothing more — "being
  // prepared" described a server that was never asked for anything.
  'p.subs.toastStatement':
    '{count} rows written to a file in your browser, exactly as they appear on this screen. The server did not prepare a statement — there is no statement route.',
  'p.subs.toastChased':
    '{count} reminders logged. Nothing was sent — no WhatsApp provider is set up.',
  'p.subs.toastChasedPartial':
    '{done} of {count} reminders logged; the rest were refused. Nothing was sent either way.',
  'p.subs.toastRecorded': 'Payment recorded for {name}.',
  // P11 · what a manual confirmation has to say (D3 — no card is stored, a
  // human is recording money that arrived somewhere else).
  'p.subs.recordMethod': 'How it arrived',
  'p.subs.recordPaidOn': 'Paid on',
  'p.subs.recordAmountHint':
    'What actually arrived. Less than the invoice is recorded as a part payment, never rounded up to the total.',
  'p.subs.recordReference': 'Reference',
  // Honest about where it goes: `invoices` has no reference column, so the
  // value survives on the audit row and nowhere else.
  'p.subs.recordReferenceHint':
    'The transaction id from the receipt. It is kept on the audit record — the invoice has nowhere to show it.',
  'p.subs.restrictReasonHint':
    'Recorded against the business, and readable by whoever asks later why it was frozen.',

  // --- P-07 ---
  'p.usage.ofUnlimited': '{used} \u00b7 unlimited',
  /* The minus sign is the message; the unit comes in already formatted. */
  'p.usage.savedGb': '\u2212{value}',
  'p.usage.emptyTitle': 'Nobody is close to a limit',
  'p.usage.emptyBody':
    'Every business has room on its plan. An upgrade worth offering would appear here.',
  // The old "Suggestion sent to {name}." said two untrue things at once: that
  // something was sent (G22 — no provider), and that it went to {name} (the
  // broadcast audience has no per-tenant filter). Both the composer and its
  // receipt now live under `p.suggest.*`, shared with P-08.
  'p.usage.dropOriginals': 'Stop keeping originals',
  'p.usage.leverConfirmPhrase': 'DROP ORIGINALS',
  // Saved is all that can honestly be claimed: the switch records the policy.
  // Nothing on the server deletes an original today (U1 is still open), so a
  // message promising a purge "tonight" would be describing a job that does
  // not exist.
  'p.usage.leverDone':
    'Saved. New uploads keep only the 720p copy. Nothing has been deleted — no job removes an original yet.',

  // --- P-08 ---
  'p.health.storageUsed': 'Storage used',
  'p.health.backupRatio': '{done}/{total}',
  // Both of these described acts that never happened. "{name} has been told the
  // upload was too large" — no route tells one owner anything, and no provider
  // is configured (G22). "Suggested the {plan} plan to {name}" — the broadcast
  // audience cannot name a tenant. Removed rather than reworded, because there
  // is no toast on this screen any more: the suggest flow shows P-11's receipt,
  // and the notify flow shows its own — which reads `delivered` off the
  // response instead of claiming anything. See the `p.notify.` block below.

  // --- P-09 ---
  'p.support.channelWHATSAPP': 'WhatsApp',
  'p.support.channelPHONE': 'Phone call',
  // TICKET_SOURCES also carries these two. The fixture invented a narrower set
  // before the contract existed; a source with no label renders its own code.
  'p.support.channelEMAIL': 'Email',
  'p.support.channelIN_APP': 'In the app',
  'p.support.businessOnTrial': '{business} \u00b7 trial',
  'p.support.open': 'Open',
  'p.support.ticketTitle': '#{number} \u00b7 {person}',
  'p.support.noteLabel': 'What was said',
  'p.support.log': 'Log',
  'p.support.logged': 'Call logged as ticket #{number}.',
  'p.support.who': 'Who called',
  'p.support.whoHint':
    'His own name, as he gave it. It need not be the account holder — often it is not.',
  // A2: `support_tickets.tenant_id` is NOT NULL, so a call from a man who is
  // not yet a customer cannot be stored. The control says so rather than
  // letting a whole conversation be typed and lost to a 400.
  'p.support.businessHint':
    'A ticket has to name a business — a call from someone with no account cannot be recorded here yet.',
  // G22, again. The row is written; nothing leaves the building.
  'p.support.replyLogged':
    'Reply logged on ticket #{number}. It has not been sent — no WhatsApp provider is set up.',
  'p.support.ticketClosed': 'Ticket #{number} closed.',
  'p.support.ticketReopened': 'Ticket #{number} reopened.',
  'p.support.reopenTicket': 'Reopen',
  'p.support.thread': 'What has been said',
  'p.support.threadEmpty': 'Nothing recorded on this ticket yet.',
  'p.support.threadNotDelivered':
    'Everything here is recorded on the ticket. None of it has been delivered to his phone — WhatsApp is not connected.',
  'p.support.saidByThem': 'He said',
  'p.support.saidByUs': '{name} replied',
  'p.support.noMatchBody': 'Try a different word, or choose another filter.',

  // --- P-10 ---
  'p.log.ticketRef': '\u00b7 #{number}',
  'p.log.ownersNotifiedValue': '{done} of {total}',
  'p.log.colTicket': 'Ticket',
  // CONTRACT GAP: P10 is a read and there is no access-log export route. The
  // file is written in the browser from the filtered rows on screen. Reading a
  // log does not change it, which is why this is the one control the screen
  // allows.
  'p.log.exportFilename': 'access-log',
  'p.log.exported':
    '{count} rows written to a CSV file in your browser \u2014 the entries this filter is showing.',
  'p.log.exportEmpty': 'No entry matches this filter, so there is nothing to export.',

  // --- P-11 ---
  'p.settings.saved': 'Settings saved.',
  'p.settings.maintenanceOn': 'Maintenance mode is on. Nobody can sign in.',
  'p.settings.broadcastPlaceholder': 'Type the message every owner will get.',

  // --- P-03 · the danger zone, once its buttons became requests ---
  // C10, decided 10 September 2026: **suspension means read-only, not lockout.**
  // Every string below now says the same thing as `p.subs.seasonNote` and
  // `p.subs.restrictMidSeason` on P-06, because they describe the same act on
  // the same server. Nothing here may promise a severity `POST …/suspend` does
  // not have: it leaves the book readable and refuses the writes.
  'p.business.suspend': 'Suspend the book',
  'p.business.suspendBody': 'They keep reading their book. Nothing in it can be changed until you lift it.',
  'p.business.suspendAction': 'Suspend',
  'p.business.suspendTitle': 'Suspend {name}?',
  'p.business.suspendWarning':
    'They can still sign in and read everything. Every change is refused until you lift it — a trader mid-season cannot record a purchase, a sale or a payment. A payment landing does not lift it; only you do.',
  'p.business.suspendReason': 'Why are you suspending them?',
  'p.business.suspendToast': '{name} is suspended. They can read their book but not change it.',
  'p.business.extendTitle': 'Extend the trial for {name}?',
  'p.business.extendDays': 'How many more days?',
  'p.business.extendDaysHint': 'Between 1 and 120, counted from the day the trial ends.',
  'p.business.extendReason': 'Why are you extending it?',
  'p.business.reasonAudited': 'Written to the audit log against your name.',
  'p.business.extendedBy': "{name}'s trial is extended by {days} days.",
  'p.business.deleteReason': 'Why are you deleting it?',
  'p.business.detailsPanel': 'Business details',
  'p.inside.animalDetails': 'Animal details',
  'p.inside.noPhotos': 'No photos or videos yet.',
  'p.inside.video': 'Video',
  'p.inside.cover': 'Cover',
  /* A derived size was never made — the trader's own file is what is on screen.
     It is his photograph either way, so this reports the pipeline, not a loss. */
  'p.inside.originalOnly': 'Original only',
  'p.inside.stillProcessing': 'Processing',
  'p.inside.mediaUnavailable': 'Could not load',
  'p.inside.mediaUnavailableCode': 'This file answered {status}.',
  'p.inside.sectionAnimal': 'The animal',
  'p.inside.sectionPurchase': 'Purchase',
  'p.inside.sectionCost': 'What it has cost',
  'p.inside.sectionSale': 'Sale',
  'p.inside.sectionExit': 'Died or lost',
  'p.inside.sectionWeights': 'Weight log',
  'p.inside.lot': 'Lot',
  'p.inside.season': 'Season',
  'p.inside.daysHeld': 'Days held',
  'p.inside.totalCost': 'Total cost',
  'p.inside.dailyBurn': 'Costing a day',
  'p.inside.saleDate': 'Sale date',
  'p.inside.buyer': 'Sold to',
  'p.inside.salePrice': 'Sale price',
  'p.inside.commission': 'Commission',
  'p.inside.profit': 'Profit',
  'p.inside.outstanding': 'Still owed',
  'p.inside.exitDate': 'Date',
  'p.inside.deathCause': 'Cause',
  'p.inside.exitLoss': 'Loss',
  'p.business.signInCode': 'Business code (for sign-in)',
  'p.business.ownerUsername': "Owner's username",
  'p.business.joinedOn': 'Joined',
  'p.business.trialEnds': 'Trial ends',
  'p.business.subscriptionStatus': 'Subscription',
  'p.business.currentPeriod': 'Current period',
  'p.business.cancelsAtPeriodEnd': 'Cancels at period end',
  'p.business.bookValue': 'Book value',
  'p.business.mediaCount': 'Photos and videos',
  'p.business.openTickets': 'Open support tickets',
  'p.business.tenantStatus.PENDING_VERIFICATION': 'Awaiting verification',
  'p.business.tenantStatus.TRIAL': 'On trial',
  'p.business.tenantStatus.ACTIVE': 'Active',
  'p.business.tenantStatus.PAST_DUE': 'Past due',
  'p.business.tenantStatus.READ_ONLY': 'Read-only',
  'p.business.tenantStatus.SUSPENDED': 'Suspended',
  'p.business.tenantStatus.CANCELLED': 'Cancelled',
  'p.business.subscriptionStatus.TRIALING': 'Trialing',
  'p.business.subscriptionStatus.ACTIVE': 'Active',
  'p.business.subscriptionStatus.PAST_DUE': 'Past due',
  'p.business.subscriptionStatus.CANCELLED': 'Cancelled',
  'p.business.reasonOptionalHint': 'Optional. Written to the audit log.',
  'p.business.suspendDefaultReason': 'Suspended from the Platform Console.',
  'p.business.deleteDefaultReason': 'Deleted from the Platform Console.',
  'p.business.deleteReasonHint':
    'Written to the audit log, which survives the deletion. Nothing else does.',

  // --- P-02 · adding a business ---
  'p.businesses.addSubtitle': 'A trader who signed up over the phone. It starts on the plan you pick.',
  'p.businesses.cityLabel': 'City',
  'p.businesses.ownerNameLabel': "Owner's name",
  'p.businesses.ownerPhoneLabel': "Owner's phone",
  'p.businesses.usernameHint': 'How the owner signs in. Letters, numbers, dot, underscore or hyphen.',
  'p.businesses.usernameInvalid': 'No spaces. Use letters, numbers, dot, underscore or hyphen — for example ali.traders.',
  'p.businesses.ownerPasswordLabel': "Owner's password",
  'p.businesses.ownerPasswordHint': 'At least 8 characters. Leave blank to have one generated.',
  'p.businesses.ownerPasswordInvalid': 'A password needs at least 8 characters.',
  'p.businesses.signInBusiness': 'Business',
  'p.businesses.trialDaysLabel': 'Trial days',
  'p.businesses.trialDaysHint': "Leave blank for the plan's own trial length.",
  'p.businesses.noPlans': 'There is no plan to put a business on yet.',
  'p.businesses.noPlansBody':
    'Price a plan on the Plans screen first. A business is created on a plan, and until one exists there is nothing to create it on.',
  'p.businesses.noteLabel': 'Note',
  'p.businesses.created': '{name} is added.',
  'p.businesses.passwordTitle': "The owner's sign-in details",
  'p.businesses.passwordBody':
    'This is the only time the password is shown. Copy these now and give them to {owner} — nobody can read the password back, and it can only be replaced.',
  'p.businesses.passwordCopied': 'Password copied.',

  // --- P-05 · creating a plan ---
  'p.plans.codeLabel': 'Plan code',
  'p.plans.codeHint': 'The code is what is stored. The name is what people read.',
  'p.plans.noCodeLeft': 'Every plan code is already in use',
  'p.plans.noCodeLeftBody':
    'A plan code is a closed set in the contract. Adding one is a contract change, not a console one.',
  'p.plans.animalLimit': 'Animals allowed',
  'p.plans.peopleLimit': 'People allowed',
  'p.plans.storageLimit': 'Storage allowed',
  'p.plans.limitHint': 'Leave blank for no limit.',
  'p.plans.storageHint': 'In GB. Leave blank for no limit.',
  'p.plans.limitInvalid': 'A whole number — digits only. Leave blank for no limit.',
  'p.plans.storageInvalid': 'A number of GB — digits only, so 1 and not 1gb. Leave blank for no limit.',
  'p.plans.featuresLabel': 'What it switches on',
  'p.plans.editPlan': 'Edit {name}',
  'p.plans.editSubtitle': 'Every book on this plan feels a limit change at once.',
  'p.plans.codeHintEdit': 'Correcting a code is safe. Inventing one is a contract change.',
  'p.plans.publicLabel': 'Offered at signup',
  'p.plans.publicHint': 'A hidden plan keeps every book already on it. It just stops being chosen.',
  'p.plans.sortLabel': 'Where it sits in the ladder',
  'p.plans.sortHint': 'Low first. Leave at 0 and the list falls back to price.',
  'p.plans.sortInvalid': 'A whole number, 0 or more.',
  'p.plans.hidden': 'Off signup',
  'p.plans.saveChangesFirst': 'Nothing has changed yet.',
  'p.plans.planSaved': '{name} saved.',
  /* "Retire", not "delete": the row stays, because invoices already raised
     against the plan still point at it and still have to resolve their price. */
  'p.plans.retire': 'Retire this plan',
  'p.plans.retireTitle': 'Retire {name}?',
  'p.plans.retireBody':
    'It stops being offered and stops being listed. Invoices already raised against it keep their price, and the code becomes free for a new plan.',
  'p.plans.retireConfirm': 'Retire it',
  'p.plans.retired': '{name} is retired.',
  'p.plans.inUseTitle': '{name} cannot be retired',
  'p.plans.inUseBody':
    '{count} businesses are on this plan and are billed by it. Taking it off signup is the move: they keep it, and nobody new can choose it.',
  'p.plans.hideFromSignup': 'Take it off signup',
  'p.plans.alreadyHidden': 'It is already off signup, so there is nothing further to do.',
  'p.plans.hiddenToast': '{name} is no longer offered at signup.',

  // --- P-11 · the writes (P17-P19) ---
  'p.settings.nothingChanged': 'Nothing on this screen has been changed.',
  'p.settings.turnOff': 'Turn off',
  'p.settings.maintenanceOffConfirmTitle': 'Turn maintenance mode off?',
  'p.settings.maintenanceOffConfirmBody':
    'Traders and their staff can sign in again as soon as this is saved.',
  // The exemption is the reason this switch can ever be turned back off. It is
  // stated on the way IN, because that is when the reader is deciding.
  'p.settings.maintenanceExempt':
    'You keep your own access to this console while it is on, which is how you turn it off again.',
  'p.settings.maintenanceOff': 'Maintenance mode is off. Everyone can sign in again.',
  'p.settings.maintenanceIsOn': 'Maintenance mode is ON — nobody can sign in.',

  // P18 · add a team member
  'p.settings.addSomeoneTitle': 'Add someone to the team',
  'p.settings.addSomeoneSubtitle':
    'They sign in to this console with the username you set here. Their first password is shown once, on the next screen.',
  'p.settings.addSomeoneSubmit': 'Add {name}',
  'p.settings.teamRoleHint':
    'Support can open a book and answer tickets. An owner can do everything on this console, including this screen.',
  'p.settings.memberAdded': '{name} can now sign in.',
  'p.settings.memberPasswordTitle': 'The first password for {name}',
  'p.settings.memberPasswordBody':
    'This is the only time it is shown. Copy it now and read it to them — nobody can read it back, and it can only be replaced.',
  'p.settings.memberPasswordSetByYou':
    'You set their password yourself, so there is nothing here to reveal.',

  // P19 · the broadcast, and what it really did
  'p.settings.broadcastSubject': 'Subject',
  'p.settings.broadcastChannel': 'Send by',
  // BROADCAST_CHANNELS carries SMS, which no ticket source does — so it has no
  // label in the P-09 block to borrow.
  'p.settings.channelSMS': 'SMS',
  'p.settings.broadcastReceiptTitle': 'Queued — nothing has been sent',
  'p.settings.broadcastQueued': 'Accepted for {count} books. Reference {id}.',
  'p.settings.broadcastNothingSent':
    'No message provider is configured, so nothing was delivered. The platform does not store this message either, so it cannot be sent later from here — it will have to be composed again once a channel is decided.',
  'p.settings.broadcastNote':
    'This queues a receipt. Delivery is undecided (G22) — no provider, no template and no consent model exists yet.',

  // P-07 / P-08 · "Suggest an upgrade" — one prefilled broadcast, two screens.
  //
  // `broadcastRequest.audience` filters on status, plan and nearLimit, and on
  // nothing else. There is NO way to address one business, so the row that was
  // clicked is a source of words and never an addressee — which is what
  // `notARecipient` exists to say, before the button is pressed.
  'p.suggest.title': 'Suggest an upgrade',
  'p.suggest.audience': 'To every book near a limit',
  'p.suggest.audienceCount': 'To all {count} books near a limit',
  'p.suggest.notARecipient':
    '{name} is where these words came from — this message is not addressed to {name}. A broadcast can only be aimed at a filter (a status, a plan, or “near a limit”), and this one is aimed at every book near a limit. Nothing here can write to a single business.',
  'p.suggest.queue': 'Queue for every book near a limit',
  'p.suggest.queueCount': 'Queue for {count} books',
  'p.suggest.subjectSeed': 'Your book is close to its limit — {plan} would fit',
  'p.suggest.bodySeed':
    'Salam. Your book is close to the limit on its current plan. Moving up to {plan} raises the limits and keeps everything already in the book — nothing is lost and nothing is re-entered. Reply and we will switch it over for you.',

  // P-08 · what the two row actions can honestly offer.
  //
  // `wouldSuit` is what a support account sees where the owner sees a button:
  // an observation, not a control that would only ever return 403.
  'p.health.wouldSuit': '{plan} would suit',
  // A failed backup is the platform's own problem — the service that builds
  // this list says so: "nobody is told; somebody investigates". So an
  // INVESTIGATE row offers no message; it states whose problem it is.
  'p.health.investigate': 'Ours to look into',
  // A row that names no book has no owner to write to — the retry line is
  // synthesised from the night's totals and belongs to no tenant.
  'p.health.noBusinessOnRow': 'Not about one book',

  // P-08 / P14 · "Notify owner" — one book, named in the path.
  //
  // Unlike the suggest flow there is no "these words came from here but go
  // elsewhere" warning to write: `POST /platform/businesses/:id/notify` takes
  // the tenant as its address, so the row that was clicked IS the recipient.
  'p.notify.addressed': 'Goes to the owner of {business}, and to nobody else.',
  'p.notify.send': 'Send to the owner',
  'p.notify.subjectSeed': 'About your book — {what}',
  'p.notify.bodySeed':
    'Salam. We are writing about something on your book — {what}. We wanted you to hear it from us rather than find it yourself. Reply here and we will sort it out with you.',
  'p.notify.reasonLabel': 'Why he is being written to',
  'p.notify.reasonHint':
    'Kept with the message in the access log, and the trader can read it in his own history — so write it for him, not for us.',
  'p.notify.receipt': 'Addressed to {name}. Reference {id}.',
  // Only ever shown when the response says `delivered` — no provider exists
  // today, so today it is never shown. It is here so that the day one is wired
  // up this screen can say so without being rewritten.
  'p.notify.sentTitle': 'Sent',
  'p.notify.wasSent': 'The provider accepted it and it has gone out by {channel}.',

  // P-06 · the statement.
  //
  // CONTRACT GAP: no statement route exists. The file is therefore written in
  // the browser from the rows on screen, and the toast says exactly that — "your
  // statement is being prepared" described a server that was never asked.
  'p.subs.statementFilename': 'subscriptions-statement',
  'p.subs.statementEmpty': 'Nothing on this screen to write — no overdue rows and no renewals.',
  'p.subs.statementLocked':
    'Amounts you are not shown are empty cells in the file, not zeroes. A blank is “not shown to you”; a 0 would be a claim about someone’s money.',
  'p.subs.csvSection': 'Section',
  'p.subs.csvOverdue': 'Overdue',
  'p.subs.csvRenewing': 'Renewing this week',
  'p.subs.csvRenewsOn': 'Renews on',
} as const satisfies Record<string, string>
